import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || "";

export const API_BASE = `${BACKEND_URL}/api`;

// Single configured axios instance so every call sends the `iaos_session`
// httpOnly cookie. `withCredentials: true` is the critical bit.
export const api = axios.create({
  baseURL: API_BASE,
  withCredentials: true,
  timeout: 30000,
});

// Normalise FastAPI's `{detail: ...}` shape into a plain string for UI display.
//
// FastAPI/Pydantic can emit `detail` in three shapes:
//   1. Plain string  — legacy or a raw HTTPException(detail="…").
//   2. List of dicts — Pydantic validator errors (`{type, loc, msg, …}`).
//   3. Object        — our backend's custom error models (`{code, message, …}`),
//      per `models/**/*Detail` definitions.
//
// Historically this helper only recognised the Pydantic `.msg` field and fell
// back to `String(detail)` for shape (3), which produces `"[object Object]"`
// in the UI on any custom error (`generation_budget_exhausted`,
// `no_proposal_content_for_coach`, insert/undo errors, etc.). We now:
//   * accept both `.message` (backend custom) and `.msg` (Pydantic)
//   * never fall through to `String(obj)` for objects — we return a generic
//     retry-friendly message instead so a future new error shape can never
//     leak an `[object Object]` into the DOM.
export function formatApiError(err) {
  const detail = err?.response?.data?.detail;
  if (detail == null) return err?.message || "Something went wrong. Please try again.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) {
    const joined = detail
      .map((e) => {
        if (!e) return null;
        if (typeof e === "string") return e;
        if (typeof e.message === "string") return e.message;
        if (typeof e.msg === "string") return e.msg;
        return null;
      })
      .filter(Boolean)
      .join(" ");
    return joined || "Something went wrong. Please try again.";
  }
  if (typeof detail === "object") {
    if (typeof detail.message === "string") return detail.message;
    if (typeof detail.msg === "string") return detail.msg;
    // Guarded fallback — never `String(obj)` which prints "[object Object]".
    return "Something went wrong. Please try again.";
  }
  return String(detail);
}

/**
 * classifyApiError — coarse-grain HTTP error kind for UI distinction.
 *
 * Admin pages want distinct copy for:
 *   - 403 Forbidden       → "You don't have permission to view this page."
 *   - 404 Not Found       → "This feature isn't available yet."
 *   - 5xx Server errors   → "Something went wrong. Try again."
 *   - Network / offline   → "You appear to be offline."
 *
 * `formatApiError` returns a raw string. This helper adds the
 * classification tag so callers can render the right shell without
 * pattern-matching on strings.
 *
 * Usage:
 *   const { kind, message } = classifyApiError(err);
 *   if (kind === "not_available") return <NotAvailablePanel />;
 */
export function classifyApiError(err) {
  const status = err?.response?.status;
  const message = formatApiError(err);
  if (status === 401) return { status, kind: "unauthenticated", message };
  if (status === 403) return { status, kind: "forbidden", message };
  if (status === 404) return { status, kind: "not_available", message };
  if (typeof status === "number" && status >= 500) return { status, kind: "server_error", message };
  if (err && !err.response) return { status: null, kind: "network", message };
  return { status, kind: "unknown", message };
}
